<template>
	<ProcessCreatePage>
		<div
			class="fixed inset-0 overflow-y-scroll bg-black bg-opacity-50 z-50"
			style="backdrop-filter: blur(5px); overscroll-behavior: contain"
			v-if="showQROptions"
		>
			<div class="flex items-start justify-center text-center my-24">
				<div
					class="bg-white rounded-lg text-center shadow-xl w-3/5 md:w-3/5 lg:w-3/5 xl:w-3/5 xxl:w-3/5 2xl:w-3/5 justify-center relative main"
					role="dialog"
					aria-modal="true"
				>
					<div class="mr-2 justify-between flex flex-col" v-if="doneInstallingApp === false">
						<div>
							<div class="flex flex-row">
								<Header :title="$t('let_customer_scan_qr').toString()" class="justify-start align-text-top" title-size="text-xl"/>
								<div class="px-2 py-4 ">
									<font-awesome-icon :icon="['fas', 'times']" class="cursor-pointer text-xl" @click="closeModal" />
								</div>
							</div>
							<div class="mb-6 mt-6">
								<div class="flex cursor-pointer justify-between" @click="showDownloadiOS = !showDownloadiOS">
									<div class="font-bold">
										{{ $t('ios') }}
									</div>
									<div>
										<font-awesome-icon :icon="['fas', showDownloadiOS ? 'angle-up' : 'angle-down']" />
									</div>
								</div>
								<div class="mt-4">
									<img src="../../../assets/qr-code-consumer-ios.svg" alt="QR Code Connect Consumer iOS" v-if="showDownloadiOS" class="h-64" />
								</div>
							</div>
							<div class="mb-6">
								<div class="flex justify-between cursor-pointer" @click="showDownloadAndroid = !showDownloadAndroid">
									<div class="font-bold">
										{{ $t('android') }}
									</div>
									<div>
										<font-awesome-icon :icon="['fas', showDownloadAndroid ? 'angle-up' : 'angle-down']" />
									</div>
								</div>
								<div class="mt-4">
									<img
										src="../../../assets/qr-code-consumer-android.svg"
										alt="QR Code Connect Consumer Android"
										v-if="showDownloadAndroid"
										class="h-64"
									/>
								</div>
							</div>
						</div>
						<div class="flex justify-end mt-10">
							<BaseButton class="mt-6 w-1/3 btn-blue" @click="doneInstallingApp = true">
								{{ $t('general.next') }}
							</BaseButton>
						</div>
					</div>
					<div class="mr-2" v-else>
						<div class="flex flex-row">
							<Header :title="$t('connect_customer_to_workshop').toString()" class="justify-start align-text-top" title-size="text-xl" />
							<div class="px-2 py-4 ">
								<font-awesome-icon :icon="['fas', 'times']" class="cursor-pointer text-xl" @click="closeModal" />
							</div>
						</div>
						<div class="mt-6">
							<vue-qrcode class="h-64" style="align-self: start" :value="getQrLink" :margin="0" />
						</div>
						<div class="flex justify-between mt-10">
							<BaseButton class="mt-6 w-1/3 mr-10" @click="doneInstallingApp = false">
								{{ $t('general.back') }}
							</BaseButton>
							<BaseButton class="mt-6 w-1/3 btn-blue" @click="fetchCustomerDetails">
								{{ $t('check_connection') }}
							</BaseButton>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div>
			<Header :title="$t('generate_form_success').toString()" title-size="text-xl" />
			<div class="main text-left" v-if="isUserAllowedInLevel5or6">
				<div>
					<BaseSelect
						ref="BaseSelect.contactOptions"
						:required="true"
						:label="$t('send_via')"
						:options="contactOptions"
						v-model="selectedContactOption"
						class="w-1/3"
						@input="checkCustomerApp"
					/>
				</div>
				<div v-if="selectedContactOption === contactOptions[0]">
					<BaseInput
						:label="$t('e_mail')"
						ref="BaseInput.email"
						:required="true"
						type="text"
						class="w-1/3"
						validationtype="email"
						:validation="regex.email"
						v-model="customerEmail"
						@input="checkContactState"
						@focusout.native="readEmailAndValidate"
						:error-message="$t('no_valid_e_mail')"
					/>
				</div>
				<div class="flex align-baseline justify-end mt-10">
					<BaseButton v-if="!isUserAllowedInLevel2" @click="provideDocuments" class="mr-10" :disabled="disableProvideDocuments">
						<font-awesome-icon :icon="['fas', 'redo']" class="mr-2" v-if="pdfURL === undefined" spin />
						{{ $t('provide_documents') }}
					</BaseButton>
					<BaseButton @click="toDamageCase(false)">{{ $t('to_damage_case') }}</BaseButton>
				</div>
			</div>
		</div>
	</ProcessCreatePage>
</template>

<script>
import BaseButton from '@/components/forms/buttons/BaseButton';
import Header from '@/components/damagecases/Header';
import { PdfApiFactory, DamagecasesApiFactory, CustomersApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import Authentication from '@/mixins/Authentication';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import { PDFTypes } from '@/api/generated/models';
import ProcessCreatePage from '@/views/management/Process/ProcessCreatePage';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import PDFRepository from '@/api/repositories/PDFRepository';
import BaseSelect from '@/components/forms/inputs/BaseSelect.vue';
import BaseInput from '@/components/forms/inputs/BaseInput.vue';
import regex from '@/helpers/Regex';
import Validate from '@/mixins/Validate';
import defaultConfiguration from '@/api/defaultConfiguration';
import VueQrcode from 'vue-qrcode';
import { _regxp } from '@/plugins/Utils';

export default {
	name: 'Finish',
	components: { VueQrcode, BaseInput, BaseSelect, ProcessCreatePage, Header, BaseButton },
	mixins: [Authentication, LoadingSpinner, AppContainerMixin, Validate],
	props: {
		isShown: { default: false, type: Boolean },
		damageCase: { required: true },
		pdfUuids: { required: false },
		hasCustomerSign: {
			type: Boolean,
			required: true,
		},
	},
	data() {
		return {
			pdfBlob: null,
			pdfURL: undefined,
			pdfName: '',
			contactOptions: [
				{ display: this.$t('e_mail'), value: 'email' },
				{ display: this.$t('customer_app'), value: 'customer_app' },
			],
			selectedContactOption: null,
			customerEmail: '',
			showQROptions: false,
			showDownloadiOS: false,
			showDownloadAndroid: false,
			doneInstallingApp: false,
			customer: null,
			disableProvideDocuments: true,
			regxp: _regxp(),
		};
	},
	methods: {
		readEmailAndValidate() {
			this.$refs['BaseInput.email'].validate();
		},
		checkCustomerApp() {
			if (this.selectedContactOption === this.contactOptions[1] && this.damageCase.customer.customer_main === null) {
				this.$swal(this.$t('general.hint').toString(), this.$t('customer_has_no_app_installed_process').toString(), 'warning').then(_ => {
					this.showQROptions = true;
					this.checkContactState();
					window.document.documentElement.classList.add('disableScroll');
				});
			} else {
				this.checkContactState();
			}
		},
		closeModal() {
			window.document.documentElement.classList.remove('disableScroll');
			this.showQROptions = false;
			this.doneInstallingApp = false;
			this.selectedContactOption = null;
		},
		provideDocuments() {
			if (this.selectedContactOption === this.contactOptions[0]) {
				if (this.validate()) {
					this.disableProvideDocuments = true;
					if (this.damageCase.customer.email) {
						this.mailDocument(this.customerEmail);
					} else {
						this.damageCase.customer.email = this.customerEmail;
						CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
							.customersIdPut(this.damageCase.customer.id, this.damageCase.customer)
							.then(result => {
								this.mailDocument(this.customerEmail);
							});
					}
				} else {
					this.$swal(this.$t('general.error').toString(), this.$t('e_mail_not_valid').toString(), 'error');
				}
			} else {
				this.disableProvideDocuments = true;
				PdfApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
					.pdfPublishinappPost({ uuids: this.damageCase.pdfUuids })
					.then(response => {
						this.disableProvideDocuments = false;
						this.$store.commit('setConfirmLeaving', false);
						this.$swal(this.$t('general.success').toString(), this.$t('shared_in_app_successfully').toString(), 'success').then(_ => {
							this.toDamageCase(false);
						});
					})
					.catch(error => {
						this.disableProvideDocuments = false;
						this.$swal(this.$t('general.error').toString(), this.$t('shared_in_app_error').toString(), 'error');
					});
			}
		},
		fetchCustomerDetails() {
			CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.customersIdGet(this.damageCase.customer.id)
				.then(response => {
					this.customer = response.data;
					if (this.customer.customer_main) {
						this.$swal(this.$t('general.success').toString(), this.$t('customer_app_connection_successful').toString(), 'success').then(_ => {
							this.showQROptions = false;
							this.checkContactState();
							window.document.documentElement.classList.remove('disableScroll');
						});
					} else {
						this.$swal(this.$t('general.error').toString(), this.$t('customer_app_connection_not_found').toString(), 'error');
					}
				})
				.catch(e => {
					this.$swal(this.$t('general.error').toString(), this.$t('error_occurred').toString(), 'error');
					this.showQROptions = false;
				});
		},
		mailDocument(mail) {
			new PDFRepository()
				.sentDocumentTo(mail, this.damageCase.pdfUuids, null)
				.then(response => {
					this.disableProvideDocuments = false;
					this.$swal(this.$t('general.success').toString(), this.$t('email_sent').toString(), 'success').then(_ => {
						this.toDamageCase(false);
					});
				})
				.catch(e => {
					this.disableProvideDocuments = false;
					this.$swal(this.$t('general.error').toString(), this.$t('email_send_error').toString(), 'error');
				});
		},
		toDamageCase(toDocuments) {
			this.$store.commit('setConfirmLeaving', false);
			this.$router.push({
				name: 'management.process.edit',
				params: { processId: this.damageCase.id, customer: this.damageCase.customer.id, scrollToDocs: toDocuments ? 'sign_documents' : false },
			});
		},
		checkContactState() {
			if (this.selectedContactOption === null) {
				this.disableProvideDocuments = true;
			}
			if (this.selectedContactOption === this.contactOptions[1]) {
				this.disableProvideDocuments = this.customer.customer_main == null;
			} else {
				const valueAsString = this.customerEmail.toString();
				const match = valueAsString.match(this.regxp.email);
				this.disableProvideDocuments = match === null || match[0] !== valueAsString;
			}
		},
	},
	computed: {
		getQrLink() {
			return 'imd-connect-app://id=' + this.damageCase?.customer.id + '&qrkey=' + this.damageCase?.customer.qrKey;
		},
		regex() {
			return regex;
		},
	},
	mounted() {
		this.setLoadingStatus(false);
		if (!this.isUserAllowedInLevel2) {
			this.customer = this.damageCase?.customer;
			this.customerEmail = this.damageCase?.customer?.email;
			PdfApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.pdfDamagecasesTypeTempSavePost(PDFTypes.DamagecaseAll, this.damageCase)
				.then(res => {
					if (res.data.uuid) {
						PdfApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
							.pdfDamagecasesUuidTempFileGet(res.data.uuid, this.damageCase.imdNetId + '_Dokumente.pdf')
							.then(result => {
								const blob = new Blob([result.data], { type: 'application/pdf' });
								if (this.isAppContainer) {
									this.pdfBlob = blob;
								}
								this.pdfName = result.headers['content-disposition'].split('filename=')[1];
								this.pdfURL = URL.createObjectURL(blob);
							});
					} else {
						this.pdfURL = null;
					}
					this.$store.commit('setConfirmLeaving', false);
					this.$store.dispatch('updateDamageCase', this.damageCase.id).then(result => {
						this.setLoadingStatus(false);
					});
				})
				.catch(() => this.setLoadingStatus(false));
		}
	},
};
</script>
